import { Toaster as Sonner, toast, useSonner } from "sonner";

type ToasterProps = React.ComponentProps<typeof Sonner>;

/**
 * How long a toast stays up. Sonner's own default is 4s, which lingers over the
 * table; anyone who needs longer has the X, and errors are also recorded on the
 * card they came from.
 */
const DURATION = 2500;

/**
 * Room under the stack for the Clear all control, so it never covers a toast.
 * Matches the control's own bottom-4 plus its height.
 */
const STACK_OFFSET = 56;

/** Sonner's own layer; the control has to share it to stay clickable. */
const TOAST_LAYER = "z-[999999999]";

/**
 * Dismisses every toast at once.
 *
 * Only appears when they are actually stacked: with one toast up, its own X is
 * the shorter path and a second control is just noise. A run that reports per-row
 * results — a backfill of twelve, say — is what makes this worth having.
 */
function ClearAll() {
    const { toasts } = useSonner();
    if (toasts.length < 2) return null;
    return (
        <div className={`fixed right-4 bottom-4 ${TOAST_LAYER}`}>
            <button
                type="button"
                onClick={() => toast.dismiss()}
                className="border-border bg-background hover:text-foreground hover:bg-muted cursor-pointer rounded-full border px-3 py-1 text-xs font-medium shadow-md transition-colors"
            >
                Clear all ({toasts.length})
            </button>
        </div>
    );
}

const Toaster = ({ ...props }: ToasterProps) => (
    <>
        <Sonner
            className="toaster group"
            duration={DURATION}
            closeButton
            offset={STACK_OFFSET}
            mobileOffset={STACK_OFFSET}
            toastOptions={{
                unstyled: true,
                classNames: {
                    // `pr-9` keeps the title clear of the close button, which is
                    // absolutely positioned because `unstyled` drops sonner's own layout.
                    // Two columns: the icon beside the text, buttons on their own row below.
                    // The icon's gap is its own margin, so a toast without one has no indent.
                    toast: "relative w-full grid grid-cols-[auto_1fr] items-start gap-y-1 rounded-xl border py-2.5 pr-9 pl-3 shadow-md font-sans",
                    icon: "mr-2 flex h-5 shrink-0 items-center",
                    content: "col-start-2 flex min-w-0 flex-col gap-1",
                    title: "font-display font-semibold text-sm text-foreground",
                    description: "text-xs text-foreground/80",
                    closeButton:
                        "absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full text-foreground/50 hover:text-foreground hover:bg-black/5 cursor-pointer transition-colors",
                    actionButton:
                        "col-span-full justify-self-end rounded-full px-4 py-1.5 font-semibold text-xs text-white cursor-pointer",
                    cancelButton:
                        "col-span-full justify-self-end rounded-full px-4 py-1.5 font-semibold text-xs cursor-pointer bg-muted text-foreground",
                    success:
                        "bg-green-100 border-green-200 dark:bg-green-950 dark:border-green-800 text-success-foreground [&>[data-icon]]:text-green-600 dark:[&>[data-icon]]:text-green-400 [&>[data-button]]:bg-green-600",
                    error: "bg-red-100 border-red-200 dark:bg-red-950 dark:border-red-800 [&>[data-button]]:bg-red-700",
                    warning:
                        "bg-orange-100 border-orange-200 dark:bg-orange-950 dark:border-orange-800 [&>[data-button]]:bg-orange-500",
                    info: "bg-blue-100 border-blue-200 dark:bg-blue-950 dark:border-blue-800 [&>[data-button]]:bg-blue-500",
                },
            }}
            {...props}
        />
        <ClearAll />
    </>
);

export { Toaster };
